<script setup>
import { ref, getCurrentInstance, onMounted, nextTick, computed, watch } from "vue";
import { useStore } from "vuex";
import * as Choices from "choices.js";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonSwitch from "@/components/ArgonSwitch.vue";
import ArgonBadge from "@/components/ArgonBadge.vue";
import ArgonAvatar from "@/components/ArgonAvatar.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import PasswordInput from "@/components/PasswordInput.vue";
import api from "@/services/api";
import photo_default from "@/assets/img/profile-picture.png";
import ChoicesSelectMultiple from "@/components/ChoicesSelectMultiple.vue";
import PDFLabels from '@/components/PDFLabels.vue';
import { useSubscription } from '@/composables/useSubscription';
import subscriptionService from '@/services/subscription.service';
import Swal from 'sweetalert2';

const { proxy } = getCurrentInstance();
const store = useStore();

// Configuración de Conekta (llave pública)
// ⚠️ IMPORTANTE: Reemplaza con tu llave pública real de Conekta
// Obtén tu llave en: https://panel.conekta.com/ → Configuración → Llaves API
const CONEKTA_PUBLIC_KEY = 'key_Psk5AQfotetd3T7bgKvhRtA'; // ← Cambia esto

// Usar composable de suscripción
const {
  currentPlan,
  planName,
  planCode,
  planPrice,
  planCurrency,
  includedModules,
  subscriptionStatus,
  isInTrial,
  daysRemaining,
  subscription,
  availablePlans,
  formatPrice,
  formatDate,
  getStatusBadge,
  isLoading,
  fetchEffectiveSubscription,
  fetchActiveModules,
  fetchAvailablePlans,
  changePlan,
  updatePaymentMethod,
  cancelSubscription,
  reactivateSubscription,
} = useSubscription();

const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  },
  roles: {
    type: Array,
    default: () => []
  },
  permissions: {
    type: Array,
    default: () => []
  }
});

const fullName = ref(props.data.user?.name || "");
const email = ref(props.data.user?.email || "");

// Usar directamente del store de Vuex (igual que el dropdown que funciona bien)
const photoUrl = ref(store.state.auth?.user?.photo_url || photo_default);

const currentPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");
const selectedPhotoFile = ref(null); // Archivo de foto seleccionado

// Estados de validación para cada campo
const errors = ref({
  name: '',
  email: '',
  specialties: '',
  photo: ''
});

// Computed para obtener la descripción del rol del usuario
const userRoleDescription = computed(() => {
  // Primero intentar obtener desde el store
  const storeUser = store.state.user;
  if (storeUser && storeUser.roles_with_descriptions && storeUser.roles_with_descriptions.length > 0) {
    const primaryRole = storeUser.roles_with_descriptions.find(role => role.is_primary) || storeUser.roles_with_descriptions[0];
    return primaryRole.description || 'Usuario';
  }

  // Backup: intentar desde getters del store
  const storeRoles = store.getters.userRoleDescriptions;
  if (storeRoles && storeRoles.length > 0) {
    const primaryRole = storeRoles.find(role => role.is_primary) || storeRoles[0];
    return primaryRole.description || 'Usuario';
  }

  // Fallback: intentar desde localStorage directamente
  const storedUserData = localStorage.getItem('user');
  if (storedUserData) {
    try {
      const userData = JSON.parse(storedUserData);
      if (userData.roles_with_descriptions && userData.roles_with_descriptions.length > 0) {
        const primaryRole = userData.roles_with_descriptions.find(role => role.is_primary)
          || userData.roles_with_descriptions[0];
        return primaryRole.description || 'Usuario';
      }
    } catch (error) {
      // Error silencioso en producción
    }
  }

  // Fallback final
  return 'Usuario del sistema';
});

// Estado para historial de pagos
const paymentHistory = ref([]);
const isLoadingPayments = ref(false);

const hasSubscriptionRecord = computed(() => Boolean(subscription.value));
const canUpdatePayment = computed(() =>
  ['active', 'trialing', 'past_due'].includes(subscriptionStatus.value)
);
const canChangePlan = computed(() =>
  ['active', 'trialing', 'past_due'].includes(subscriptionStatus.value)
);
const canChangeBillingCycle = computed(() =>
  ['active', 'trialing', 'past_due'].includes(subscriptionStatus.value)
);
const canCancelSubscription = computed(() =>
  ['active', 'trialing', 'past_due'].includes(subscriptionStatus.value)
);
const canReactivate = computed(() => {
  if (subscriptionStatus.value !== 'cancelled') return false;
  const endsAt = subscription.value?.ends_at;
  if (!endsAt) return true;
  return new Date(endsAt).getTime() > Date.now();
});

// Nuevo: mostrar botón de pago manual cuando:
// - Está en trial y puede pagar antes
// - La suscripción está vencida y necesita reactivar
const canPayNow = computed(() => {
  const status = subscriptionStatus.value;
  return status === 'trialing' || status === 'expired' || (status === 'cancelled' && canReactivate.value);
});

// Inicializar Conekta con la llave pública
onMounted(() => {
  if (window.Conekta) {
    window.Conekta.setPublicKey(CONEKTA_PUBLIC_KEY);
    console.log('✅ Conekta inicializado');
  } else {
    console.error('❌ Conekta.js no está cargado');
  }
});

const hasSubscriptionActions = computed(
  () =>
    canChangePlan.value ||
    canUpdatePayment.value ||
    canChangeBillingCycle.value ||
    canCancelSubscription.value ||
    canReactivate.value
);

const primaryPaymentMethod = computed(() => {
  const methods = subscription.value?.payment_methods;
  if (!Array.isArray(methods) || methods.length === 0) return '';
  const method = methods[0];
  const brand = method.brand || method.card_brand || method.type || '';
  const last4 = method.last4 || method.last_four || '';
  const expMonth = method.exp_month || '';
  const expYear = method.exp_year || '';
  const exp = expMonth && expYear ? `${expMonth}/${expYear}` : '';
  const label = [brand, last4 ? `**** ${last4}` : '', exp ? `· ${exp}` : '']
    .filter(Boolean)
    .join(' ');
  return label.trim();
});

const conektaCustomerId = computed(() => {
  const resolveId = (user) =>
    user?.conekta_customer_id ||
    user?.conekta_customer?.id ||
    user?.conekta_customer?.customer_id ||
    user?.application?.conekta_customer_id ||
    user?.application?.conekta_customer?.id ||
    user?.applications?.[0]?.conekta_customer_id ||
    '';

  const authUser = store.state.auth?.user;
  const rootUser = store.state.user;
  const propsUser = props.data.user;
  const subscriptionId =
    subscription.value?.conekta_customer_id || subscription.value?.customer_id || '';

  const id =
    resolveId(authUser) ||
    resolveId(rootUser) ||
    resolveId(propsUser) ||
    subscriptionId;
  if (id) return id;

  const storedUserData = localStorage.getItem('user');
  if (!storedUserData) return '';
  try {
    const userData = JSON.parse(storedUserData);
    return resolveId(userData);
  } catch (error) {
    return '';
  }
});

// Métodos para gestión de suscripción
const refreshSubscription = async (showToast = true) => {
  try {
    await fetchEffectiveSubscription();
    await fetchActiveModules({ per_page: 100 });

    if (showToast) {
      Swal.fire({
        icon: 'success',
        title: 'Actualizado',
        text: 'Información actualizada correctamente',
        timer: 2000,
        showConfirmButton: false,
      });
    }
  } catch (error) {
    console.error('Error al actualizar:', error);
  }
};

const loadPaymentHistory = async () => {
  isLoadingPayments.value = true;
  try {
    const response = await subscriptionService.getPaymentHistory({
      per_page: 20,
      page: 1
    });

    console.log('💳 Historial de pagos recibido:', response);

    // El backend devuelve { payments: [], message: "..." }
    paymentHistory.value = response.payments || [];

    if (response.message && paymentHistory.value.length === 0) {
      console.log('📝', response.message);
    }
  } catch (error) {
    console.warn('⚠️ Historial de pagos no disponible:', error);

    // Si el endpoint no existe (404), no mostrar error al usuario
    if (error.response?.status === 404) {
      console.log('📝 El endpoint de historial de pagos aún no está implementado');
      paymentHistory.value = [];
    } else {
      console.error('Error al cargar historial:', error);
      paymentHistory.value = [];
    }
  } finally {
    isLoadingPayments.value = false;
  }
};

const handleChangePlan = async () => {
  if (!hasSubscriptionRecord.value) {
    Swal.fire({
      icon: 'info',
      title: 'Sin suscripción',
      text: 'No tienes una suscripción activa para cambiar de plan.',
    });
    return;
  }

  try {
    if (!availablePlans.value.length) {
      await fetchAvailablePlans();
    }
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudieron cargar los planes disponibles.',
    });
    return;
  }

  const plans = availablePlans.value || [];
  if (!plans.length) {
    Swal.fire({
      icon: 'info',
      title: 'Sin planes',
      text: 'No hay planes disponibles en este momento.',
    });
    return;
  }

  const currentCode = planCode.value || subscription.value?.plan?.code || '';
  const currentCycle = subscription.value?.billing_cycle || 'monthly';
  const escapeAttr = (value) => String(value).replace(/"/g, '&quot;');
  const planOptions = plans
    .map((plan) => {
      const monthlyPrice = plan.price_monthly || plan.base_price || 0;
      const yearlyPrice = plan.price_yearly || plan.base_price_yearly || monthlyPrice;
      const currency = plan.currency || planCurrency.value || 'MXN';
      const monthlyLabel = `${plan.name} - ${formatPrice(monthlyPrice, currency)}/mes`;
      const yearlyLabel = `${plan.name} - ${formatPrice(yearlyPrice, currency)}/año (17% descuento)`;
      const label = currentCycle === 'yearly' ? yearlyLabel : monthlyLabel;
      const selected = plan.code === currentCode ? 'selected' : '';
      return `
        <option
          value="${plan.code}"
          ${selected}
          data-monthly-label="${escapeAttr(monthlyLabel)}"
          data-yearly-label="${escapeAttr(yearlyLabel)}"
        >
          ${label}
        </option>
      `;
    })
    .join('');

  const updatePlanOptionLabels = (cycle) => {
    const popup = Swal.getPopup();
    if (!popup) return;
    const select = popup.querySelector('#plan-code');
    if (!select) return;
    Array.from(select.options).forEach((option) => {
      const monthlyLabel = option.getAttribute('data-monthly-label') || option.textContent;
      const yearlyLabel = option.getAttribute('data-yearly-label') || option.textContent;
      option.textContent = cycle === 'yearly' ? yearlyLabel : monthlyLabel;
    });
    const note = popup.querySelector('#billing-discount-note');
    if (note) {
      note.textContent =
        cycle === 'yearly'
          ? 'Incluye 17% de descuento por pago anual.'
          : 'Facturación mensual sin descuento anual.';
    }
  };

  const result = await Swal.fire({
    icon: 'question',
    title: 'Cambiar plan',
    html: `
      <div style="text-align: left; margin-bottom: 20px;">
        <label class="swal2-label" for="plan-code" style="display: block; margin-bottom: 8px; font-weight: 600;">Plan</label>
        <select id="plan-code" class="swal2-input" style="width: 100%; margin: 0;">${planOptions}</select>
      </div>
      <div style="text-align: left; margin-bottom: 20px;">
        <label class="swal2-label" for="billing-cycle" style="display: block; margin-bottom: 8px; font-weight: 600;">Ciclo de facturación</label>
        <select id="billing-cycle" class="swal2-input" style="width: 100%; margin: 0;">
          <option value="monthly" ${currentCycle === 'monthly' ? 'selected' : ''}>Mensual</option>
          <option value="yearly" ${currentCycle === 'yearly' ? 'selected' : ''}>Anual</option>
        </select>
      </div>
      <p id="billing-discount-note" style="font-size: 0.875rem; color: #6c757d; margin-top: 8px; margin-bottom: 8px;"></p>
      <p style="font-size: 0.875rem; color: #6c757d; margin-top: 8px;">
        Los cambios se aplicarán en tu próximo ciclo de facturación.
      </p>
    `,
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: 'Cambiar plan',
    cancelButtonText: 'Cancelar',
    didOpen: () => {
      const popup = Swal.getPopup();
      const cycleSelect = popup?.querySelector('#billing-cycle');
      updatePlanOptionLabels(cycleSelect?.value || currentCycle);
      cycleSelect?.addEventListener('change', (event) => {
        updatePlanOptionLabels(event.target.value);
      });
    },
    preConfirm: () => {
      const popup = Swal.getPopup();
      const plan_code = popup.querySelector('#plan-code')?.value;
      const billing_cycle = popup.querySelector('#billing-cycle')?.value;
      if (!plan_code) {
        Swal.showValidationMessage('Selecciona un plan.');
        return false;
      }
      if (!billing_cycle) {
        Swal.showValidationMessage('Selecciona un ciclo de facturación.');
        return false;
      }
      return { plan_code, billing_cycle };
    },
  });

  if (!result.isConfirmed || !result.value) return;

  const { plan_code, billing_cycle } = result.value;
  if (plan_code === currentCode && billing_cycle === currentCycle) {
    Swal.fire({
      icon: 'info',
      title: 'Sin cambios',
      text: 'Selecciona un plan o ciclo diferente para continuar.',
    });
    return;
  }

  try {
    Swal.fire({
      title: 'Actualizando plan...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    const response = await changePlan({ plan_code, billing_cycle });
    await refreshSubscription(false);
    Swal.fire({
      icon: 'success',
      title: 'Plan actualizado',
      text:
        response?.data?.message ||
        'El cambio de plan fue registrado correctamente.',
    });
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.response?.data?.message || 'No se pudo cambiar el plan.',
    });
  }
};

const handleUpdatePaymentMethod = async () => {
  const result = await Swal.fire({
    title: 'Actualizar método de pago',
    html: `
      <input id="card-name" class="swal2-input" placeholder="Nombre del titular" autocomplete="cc-name">
      <input id="card-number" class="swal2-input" placeholder="Número de tarjeta" inputmode="numeric" autocomplete="cc-number">
      <div style="display:flex; gap:8px;">
        <input id="card-exp-month" class="swal2-input" placeholder="MM" inputmode="numeric" autocomplete="cc-exp-month" style="width: 33%;">
        <input id="card-exp-year" class="swal2-input" placeholder="YYYY" inputmode="numeric" autocomplete="cc-exp-year" style="width: 33%;">
        <input id="card-cvc" class="swal2-input" placeholder="CVC" inputmode="numeric" autocomplete="cc-csc" style="width: 33%;">
      </div>
    `,
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: 'Guardar tarjeta',
    cancelButtonText: 'Cancelar',
    preConfirm: async () => {
      const popup = Swal.getPopup();
      const name = popup.querySelector('#card-name')?.value.trim();
      const numberRaw = popup.querySelector('#card-number')?.value || '';
      const expMonthRaw = popup.querySelector('#card-exp-month')?.value || '';
      const expYearRaw = popup.querySelector('#card-exp-year')?.value || '';
      const cvcRaw = popup.querySelector('#card-cvc')?.value || '';

      const number = numberRaw.replace(/\D/g, '');
      const expMonth = expMonthRaw.replace(/\D/g, '');
      const expYear = expYearRaw.replace(/\D/g, '');
      const cvc = cvcRaw.replace(/\D/g, '');

      if (!name) {
        Swal.showValidationMessage('Ingresa el nombre del titular.');
        return false;
      }
      if (number.length < 13 || number.length > 19) {
        Swal.showValidationMessage('El número de tarjeta no es válido.');
        return false;
      }
      const monthInt = parseInt(expMonth, 10);
      if (!monthInt || monthInt < 1 || monthInt > 12) {
        Swal.showValidationMessage('El mes de vencimiento no es válido.');
        return false;
      }
      let year = expYear;
      if (year.length === 2) {
        year = `20${year}`;
      }
      if (year.length !== 4) {
        Swal.showValidationMessage('El año de vencimiento no es válido.');
        return false;
      }
      if (cvc.length < 3 || cvc.length > 4) {
        Swal.showValidationMessage('El CVC no es válido.');
        return false;
      }

      // Retornar datos de tarjeta para tokenizar con Conekta
      return {
        number,
        name,
        exp_month: expMonth,
        exp_year: year,
        cvc
      };
    },
  });

  if (!result.isConfirmed || !result.value) return;

  try {
    Swal.fire({
      title: 'Guardando método de pago...',
      html: '<p>Validando tu tarjeta de forma segura...</p>',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    // Tokenizar con Conekta.js
    const cardData = result.value;

    const tokenResponse = await new Promise((resolve, reject) => {
      if (!window.Conekta) {
        reject(new Error('Conekta.js no está disponible'));
        return;
      }

      window.Conekta.Token.create(
        { card: cardData },
        (token) => {
          console.log('✅ Token obtenido:', token.id);
          resolve(token);
        },
        (error) => {
          console.error('❌ Error al tokenizar:', error);
          reject(new Error(error.message_to_purchaser || 'Error al procesar la tarjeta'));
        }
      );
    });

    // Enviar SOLO el token al backend (NUNCA los datos de tarjeta)
    const response = await updatePaymentMethod({
      token_id: tokenResponse.id
    });
    await refreshSubscription(false);
    Swal.fire({
      icon: 'success',
      title: 'Método actualizado',
      text:
        response?.message ||
        'El método de pago se actualizó correctamente.',
    });
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text:
        error.response?.data?.message ||
        error.message ||
        'No se pudo actualizar el método de pago.',
    });
  }
};

/**
 * Permite al usuario pagar manualmente la suscripción
 * Útil cuando:
 * - Está en trial y quiere activar antes
 * - La suscripción venció y quiere reactivar
 */
const handlePayNow = async () => {
  const isExpired = subscriptionStatus.value === 'expired' || subscriptionStatus.value === 'cancelled';
  const title = isExpired ? 'Reactivar Suscripción' : 'Pagar Ahora';
  const confirmText = isExpired ? 'Pagar y Reactivar' : 'Pagar Ahora';

  const result = await Swal.fire({
    icon: isExpired ? 'warning' : 'question',
    title,
    width: '520px',
    html: `
      <div style="text-align: center; margin-bottom: 16px;">
        <p style="margin-bottom: 12px; font-size: 0.9rem; color: #475569;">
          ${isExpired
        ? 'Tu suscripción ha vencido. Agrega un método de pago para reactivarla.'
        : 'Paga ahora y activa tu suscripción de inmediato.'}
        </p>
        <div style="display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; background: #ecfdf5; border: 2px solid #22c55e; border-radius: 10px; margin-bottom: 16px;">
          <i class="fas fa-tag" style="color: #16a34a; font-size: 1rem;"></i>
          <span style="font-weight: 700; font-size: 1.15rem; color: #16a34a;">
            ${formatPrice(planPrice.value, planCurrency.value)}
          </span>
        </div>
      </div>

      <div style="max-width: 420px; margin: 0 auto;">
        <div style="text-align: left; margin-bottom: 12px;">
          <label style="display: block; margin-bottom: 4px; font-size: 0.8rem; font-weight: 600; color: #334155;">
            Nombre del titular
          </label>
          <input
            id="card-name"
            class="swal2-input"
            placeholder="Como aparece en la tarjeta"
            autocomplete="cc-name"
            style="width: 100%; margin: 0; padding: 10px 14px; font-size: 0.9rem;"
          >
        </div>

        <div style="text-align: left; margin-bottom: 12px;">
          <label style="display: block; margin-bottom: 4px; font-size: 0.8rem; font-weight: 600; color: #334155;">
            Número de tarjeta
          </label>
          <input
            id="card-number"
            class="swal2-input"
            placeholder="1234 5678 9012 3456"
            inputmode="numeric"
            autocomplete="cc-number"
            style="width: 100%; margin: 0; padding: 10px 14px; font-size: 0.9rem;"
          >
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 12px;">
          <div style="text-align: left;">
            <label style="display: block; margin-bottom: 4px; font-size: 0.8rem; font-weight: 600; color: #334155;">
              Mes
            </label>
            <input
              id="card-exp-month"
              class="swal2-input"
              placeholder="MM"
              inputmode="numeric"
              autocomplete="cc-exp-month"
              style="width: 100%; margin: 0; padding: 10px; font-size: 0.9rem; text-align: center;"
            >
          </div>
          <div style="text-align: left;">
            <label style="display: block; margin-bottom: 4px; font-size: 0.8rem; font-weight: 600; color: #334155;">
              Año
            </label>
            <input
              id="card-exp-year"
              class="swal2-input"
              placeholder="YYYY"
              inputmode="numeric"
              autocomplete="cc-exp-year"
              style="width: 100%; margin: 0; padding: 10px; font-size: 0.9rem; text-align: center;"
            >
          </div>
          <div style="text-align: left;">
            <label style="display: block; margin-bottom: 4px; font-size: 0.8rem; font-weight: 600; color: #334155;">
              CVC
            </label>
            <input
              id="card-cvc"
              class="swal2-input"
              placeholder="123"
              inputmode="numeric"
              autocomplete="cc-csc"
              style="width: 100%; margin: 0; padding: 10px; font-size: 0.9rem; text-align: center;"
            >
          </div>
        </div>

        <div style="display: flex; align-items: center; gap: 6px; padding: 8px 12px; background: #f8fafc; border-radius: 6px; margin-top: 12px;">
          <i class="fas fa-lock" style="color: #64748b; font-size: 0.85rem;"></i>
          <span style="font-size: 0.75rem; color: #64748b; font-weight: 500;">
            Pago 100% seguro. Tu tarjeta será guardada para futuros pagos.
          </span>
        </div>
      </div>
    `,
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: 'Cancelar',
    preConfirm: async () => {
      const popup = Swal.getPopup();
      const name = popup.querySelector('#card-name')?.value.trim();
      const numberRaw = popup.querySelector('#card-number')?.value || '';
      const expMonthRaw = popup.querySelector('#card-exp-month')?.value || '';
      const expYearRaw = popup.querySelector('#card-exp-year')?.value || '';
      const cvcRaw = popup.querySelector('#card-cvc')?.value || '';

      const number = numberRaw.replace(/\D/g, '');
      const expMonth = expMonthRaw.replace(/\D/g, '');
      const expYear = expYearRaw.replace(/\D/g, '');
      const cvc = cvcRaw.replace(/\D/g, '');

      if (!name) {
        Swal.showValidationMessage('Ingresa el nombre del titular.');
        return false;
      }
      if (number.length < 13 || number.length > 19) {
        Swal.showValidationMessage('El número de tarjeta no es válido.');
        return false;
      }
      const monthInt = parseInt(expMonth, 10);
      if (!monthInt || monthInt < 1 || monthInt > 12) {
        Swal.showValidationMessage('El mes de vencimiento no es válido.');
        return false;
      }
      let year = expYear;
      if (year.length === 2) {
        year = `20${year}`;
      }
      if (year.length !== 4) {
        Swal.showValidationMessage('El año de vencimiento no es válido.');
        return false;
      }
      if (cvc.length < 3 || cvc.length > 4) {
        Swal.showValidationMessage('El CVC no es válido.');
        return false;
      }

      // Retornar datos de tarjeta para tokenizar con Conekta
      return {
        number,
        name,
        exp_month: expMonth,
        exp_year: year,
        cvc
      };
    },
  });

  if (!result.isConfirmed || !result.value) return;

  try {
    // Mostrar loading mientras se tokeniza
    Swal.fire({
      title: 'Procesando pago...',
      html: '<p>Validando tu tarjeta de forma segura...</p>',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    // Tokenizar con Conekta.js
    const cardData = result.value;

    const tokenResponse = await new Promise((resolve, reject) => {
      if (!window.Conekta) {
        reject(new Error('Conekta.js no está disponible'));
        return;
      }

      window.Conekta.Token.create(
        { card: cardData },
        (token) => {
          console.log('✅ Token obtenido:', token.id);
          resolve(token);
        },
        (error) => {
          console.error('❌ Error al tokenizar:', error);
          reject(new Error(error.message_to_purchaser || 'Error al procesar la tarjeta'));
        }
      );
    });

    // Actualizar loading
    Swal.update({
      html: '<p>Procesando tu pago...</p>',
    });

    // Enviar SOLO el token al backend (NUNCA los datos de tarjeta)
    const response = await subscriptionService.paySubscriptionManually({
      token_id: tokenResponse.id,
      pay_now: true
    });

    // Refrescar suscripción y historial de pagos
    await refreshSubscription(false);
    await loadPaymentHistory();

    // Mostrar éxito con detalles del pago
    const payment = response.payment || {};
    const message = response.message || 'Tu pago ha sido procesado correctamente.';
    Swal.fire({
      icon: 'success',
      title: '¡Pago Exitoso!',
      html: `
        <p>${message}</p>
        <div class="text-start mt-3">
          <p class="mb-1"><strong>Monto:</strong> ${formatPrice(payment.amount || planPrice.value, payment.currency || planCurrency.value)}</p>
          <p class="mb-1"><strong>Estado:</strong> <span class="badge bg-success">Pagado</span></p>
          <p class="mb-0"><strong>Tu suscripción está activa</strong></p>
        </div>
      `,
      confirmButtonText: 'Entendido',
    });
  } catch (error) {
    console.error('Error al procesar pago:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error al Procesar Pago',
      text:
        error.response?.data?.message ||
        error.message ||
        'No se pudo procesar el pago. Por favor intenta nuevamente.',
    });
  }
};

const handleChangeBillingCycle = async () => {
  const currentCycle = subscription.value?.billing_cycle || 'monthly';
  const newCycle = currentCycle === 'monthly' ? 'yearly' : 'monthly';
  const label = newCycle === 'yearly' ? 'anual' : 'mensual';
  const plan_code = planCode.value || subscription.value?.plan?.code;

  if (!plan_code) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudo identificar tu plan actual.',
    });
    return;
  }

  const result = await Swal.fire({
    icon: 'question',
    title: `¿Cambiar a facturación ${label}?`,
    html:
      currentCycle === 'monthly'
        ? '<p>Al cambiar a facturación anual <strong>ahorrarás 2 meses</strong> (17% de descuento).</p>'
        : '<p>Volverás a la facturación mensual sin compromiso a largo plazo.</p>',
    showCancelButton: true,
    confirmButtonText: 'Sí, cambiar',
    cancelButtonText: 'Cancelar',
  });

  if (!result.isConfirmed) return;

  try {
    Swal.fire({
      title: 'Actualizando ciclo...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    const response = await changePlan({ plan_code, billing_cycle: newCycle });
    await refreshSubscription(false);
    Swal.fire({
      icon: 'success',
      title: 'Ciclo actualizado',
      text:
        response?.data?.message ||
        'El ciclo de facturación fue actualizado.',
    });
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text:
        error.response?.data?.message ||
        'No se pudo cambiar el ciclo de facturación.',
    });
  }
};

const handleCancelSubscription = async () => {
  const result = await Swal.fire({
    icon: 'warning',
    title: '¿Cancelar suscripción?',
    html: `
      <p>Tu suscripción permanecerá activa hasta el final del período de facturación actual.</p>
      <p class="text-sm text-danger"><strong>Nota:</strong> Perderás acceso a todos los módulos al finalizar el período.</p>
    `,
    input: 'textarea',
    inputLabel: 'Motivo de la cancelación (opcional)',
    inputPlaceholder: 'Cuéntanos por qué cancelas...',
    showCancelButton: true,
    confirmButtonText: 'Sí, cancelar',
    cancelButtonText: 'No, mantener',
    confirmButtonColor: '#d33',
  });

  if (result.isConfirmed) {
    try {
      await cancelSubscription({
        reason: result.value || '',
        cancel_immediately: false
      });

      Swal.fire({
        icon: 'success',
        title: 'Suscripción cancelada',
        text: 'Tu suscripción ha sido cancelada. Tendrás acceso hasta el final del período actual.',
      });

      // Actualizar información
      await refreshSubscription(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'No se pudo cancelar la suscripción. Intenta nuevamente.',
      });
    }
  }
};

const handleReactivateSubscription = async () => {
  const result = await Swal.fire({
    icon: 'question',
    title: '¿Reactivar suscripción?',
    text: 'Tu suscripción volverá a estar activa inmediatamente.',
    showCancelButton: true,
    confirmButtonText: 'Sí, reactivar',
    cancelButtonText: 'Cancelar',
  });

  if (!result.isConfirmed) return;

  try {
    Swal.fire({
      title: 'Reactivando...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    const response = await reactivateSubscription();
    await refreshSubscription(false);
    Swal.fire({
      icon: 'success',
      title: 'Suscripción reactivada',
      text: response?.data?.message || 'Tu suscripción fue reactivada.',
    });
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text:
        error.response?.data?.message ||
        'No se pudo reactivar la suscripción.',
    });
  }
};

// Helpers para estilos y estados
const getStatusIconClass = () => {
  const status = subscriptionStatus.value;
  if (status === 'active') return 'status-icon-active';
  if (status === 'trialing') return 'status-icon-trial';
  if (status === 'past_due') return 'status-icon-warning';
  return 'status-icon-inactive';
};

const getStatusIcon = () => {
  const status = subscriptionStatus.value;
  if (status === 'active') return 'fas fa-check-circle';
  if (status === 'trialing') return 'fas fa-gift';
  if (status === 'past_due') return 'fas fa-exclamation-triangle';
  return 'fas fa-times-circle';
};

const getPaymentStatusClass = (status) => {
  if (status === 'paid') return 'payment-icon-success';
  if (status === 'pending') return 'payment-icon-pending';
  if (status === 'refunded') return 'payment-icon-refunded';
  return 'payment-icon-failed';
};

const getPaymentIcon = (status) => {
  if (status === 'paid') return 'fas fa-check';
  if (status === 'pending') return 'fas fa-clock';
  if (status === 'refunded') return 'fas fa-undo-alt';
  return 'fas fa-times';
};

const getPaymentBadgeClass = (status) => {
  if (status === 'paid') return 'bg-success';
  if (status === 'pending') return 'bg-warning';
  if (status === 'refunded') return 'bg-secondary';
  return 'bg-danger';
};

const getPaymentAmountClass = (status) => {
  if (status === 'paid') return 'text-success';
  if (status === 'pending') return 'text-warning';
  if (status === 'refunded') return 'text-secondary';
  return 'text-danger';
};

const getPaymentStatusLabel = (status) => {
  const labels = {
    paid: 'Pagado',
    failed: 'Fallido',
    pending: 'Pendiente',
    refunded: 'Reembolsado',
  };
  return labels[status] || status;
};

const updatePassword = async () => {
  if (!currentPassword.value || !newPassword.value || !confirmPassword.value) {
    proxy.$notify("Por favor, completa todos los campos.", "", "warning", "ni ni-alert-circle-exc ni-lg");
    return;
  }
  if (newPassword.value !== confirmPassword.value) {
    proxy.$notify("Las contraseñas nuevas no coinciden.", "", "danger", "ni ni-fat-remove ni-lg");
    return;
  }
  if (newPassword.value.length < 6) {
    proxy.$notify("La nueva contraseña debe tener al menos 6 caracteres.", "", "warning", "ni ni-alert-circle-exc ni-lg");
    return;
  }
  try {
    const response = await api.post(`/users/${props.data.user.id}/update-password`, {
      current_password: currentPassword.value,
      new_password: newPassword.value,
      new_password_confirmation: confirmPassword.value
    });
    proxy.$notify(response.data.message || "Contraseña actualizada correctamente.", "", "success", "ni ni-like-2 ni-lg");
    currentPassword.value = "";
    newPassword.value = "";
    confirmPassword.value = "";
  } catch (error) {
    proxy.$notify(
      error.response?.data?.message || "Error al actualizar la contraseña.",
      "",
      "danger",
      "ni ni-fat-remove ni-lg"
    );
  }
};

const selectedSpecialties = ref(
  props.data.user?.doctor?.specialties ? props.data.user.doctor.specialties.map(s => s.id) : []
);

const specialtiesOptions = ref([]);

const destroyChoices = (elementId) => {
  const element = document.getElementById(elementId);
  if (element && element.choicesInstance) {
    element.choicesInstance.destroy();
  }
};

const initializeChoices = async (elementId) => {
  await nextTick();
  const element = document.getElementById(elementId);
  if (element && !element.classList.contains('choices__input')) {
    element.choicesInstance = new Choices(element, { allowHTML: true });
  }
};

const fetchSpecialties = async () => {
  try {
    const response = await api.get("/specialties");

    if (response.data && response.data.data) {
      specialtiesOptions.value = response.data.data.map(s => ({
        text: s.name,
        value: s.id
      }));
    } else {
      specialtiesOptions.value = [];
    }

    await nextTick();
    destroyChoices("choices-specialties");
    initializeChoices("choices-specialties");

  } catch (error) {
    console.error("Error al obtener las especialidades:", error);
  }
};

// Validar tamaño (máx 5MB) - comentado por ahora
/*
const handlePhotoSelect = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validar tipo de archivo
  if (!file.type.startsWith('image/')) {
    errors.value.photo = 'Por favor selecciona una imagen válida';
    return;
  }

  // Validar tamaño (máx 5MB)
  if (file.size > 5 * 1024 * 1024) {
    errors.value.photo = 'La imagen no debe superar 5MB';
    return;
  }

  errors.value.photo = '';
  selectedPhotoFile.value = file;

  // Crear vista previa inmediata
  const reader = new FileReader();
  reader.onload = (e) => {
    photoUrl.value = e.target.result;
  };
  reader.readAsDataURL(file);
};
*/

onMounted(async () => {
  await fetchSpecialties();

  console.log('🔍 SideNavItem mounted - Valores iniciales:', {
    hasSubscriptionRecord: hasSubscriptionRecord.value,
    subscriptionStatus: subscriptionStatus.value,
    planName: planName.value,
    currentPlan: currentPlan.value
  });

  // Cargar información de suscripción y pagos
  if (hasSubscriptionRecord.value) {
    await loadPaymentHistory();
  }
});

watch(hasSubscriptionRecord, async (hasSubscription) => {
  if (hasSubscription && paymentHistory.value.length === 0) {
    await loadPaymentHistory();
  }
});

// Watch para detectar cambios en la suscripción
watch([hasSubscriptionRecord, subscriptionStatus, planName], ([hasRecord, status, name]) => {
  console.log('🔍 SideNavItem - Cambio detectado:', {
    hasSubscriptionRecord: hasRecord,
    subscriptionStatus: status,
    planName: name
  });
});

const updateProfile = async () => {
  // Limpiar errores previos
  errors.value = {
    name: '',
    email: '',
    specialties: '',
    photo: ''
  };

  try {
    // 🎯 Preparar datos como FormData para incluir la foto
    const formData = new FormData();
    formData.append('name', fullName.value);
    formData.append('email', email.value);
    // Agregar especialidades como array
    selectedSpecialties.value.forEach((id, index) => {
      formData.append(`specialties[${index}]`, id);
    });

    // Agregar foto si se seleccionó una nueva
    if (selectedPhotoFile.value) {
      formData.append('photo', selectedPhotoFile.value);
    }

    // 🎯 UN SOLO ENDPOINT que actualiza todo (usuario + doctor + especialidades + foto)
    const response = await api.post('/profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: {
        _method: 'PUT' // Laravel method spoofing para PUT con FormData
      }
    });

    // ✅ Actualizar Vuex store con los datos actualizados del servidor
    const updatedUser = response.data.data;

    // Actualizar URL de la foto desde el servidor
    if (updatedUser.photo_url) {
      photoUrl.value = updatedUser.photo_url;
    }

    // Limpiar archivo seleccionado después de subir exitosamente
    selectedPhotoFile.value = null;

    // ✅ Actualizar ambos stores para que se refleje en todo el sistema
    store.commit('auth/setUser', updatedUser);
    store.commit('setUser', updatedUser); // Store principal (para el dropdown)

    proxy.$notify(response.data.message, "", "success", "ni ni-like-2 ni-lg");

  } catch (error) {
    console.error('Error al actualizar el perfil:', error);

    // 🔥 Manejar errores de validación del backend
    if (error.response?.status === 422) {
      const backendErrors = error.response.data.errors;
      const message = error.response.data.message;

      // Asignar errores a cada campo
      if (backendErrors) {
        if (backendErrors.name) {
          errors.value.name = backendErrors.name[0];
        }
        if (backendErrors.email) {
          errors.value.email = backendErrors.email[0];
        }
        if (backendErrors.specialties) {
          errors.value.specialties = backendErrors.specialties[0];
        }
        if (backendErrors.photo) {
          errors.value.photo = backendErrors.photo[0];
          // Restaurar foto original si hay error
          photoUrl.value = props.data.user?.photo_url || photo_default;
          selectedPhotoFile.value = null;
        }
      }

      // Mostrar solo el mensaje pal (sin duplicar)
      proxy.$notify(
        message || "Error de validación",
        "",
        "danger",
        "ni ni-fat-remove ni-lg"
      );
    } else {
      // Error genérico
      proxy.$notify(
        error.response?.data?.message || "Error al actualizar el perfil.",
        "",
        "danger",
        "ni ni-fat-remove ni-lg"
      );
    }
  }
};

const updateProfilePhoto = async (event) => {
  const file = event.target.files[0];
  if (!file) {
    proxy.$notify("Por favor, seleccione una foto para subir.", "", "warning", "ni ni-alert-circle-exc ni-lg");
    return;
  }

  // Validar tipo de archivo
  if (!file.type.startsWith('image/')) {
    proxy.$notify("Por favor selecciona una imagen válida.", "", "warning", "ni ni-alert-circle-exc ni-lg");
    return;
  }

  // Validar tamaño (máx 5MB)
  if (file.size > 5 * 1024 * 1024) {
    proxy.$notify("La imagen no debe superar 5MB.", "", "warning", "ni ni-alert-circle-exc ni-lg");
    return;
  }

  // Vista previa inmediata
  const reader = new FileReader();
  reader.onload = (e) => {
    photoUrl.value = e.target.result;
  };
  reader.readAsDataURL(file);

  const formData = new FormData();
  formData.append('photo', file);

  try {
    // 🎯 Nueva API para usuario autenticado
    const response = await api.post('/profile/photo', formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });

    // Actualizar con la URL del servidor
    const updatedData = response.data.data;
    photoUrl.value = updatedData.photo_url;

    // Actualizar el store completo con el rio actualizado
    const currentUser = store.state.auth.user;
    const updatedUser = {
      ...currentUser,
      photo_url: updatedData.photo_url,
      photo: updatedData.photo
    };

    // ✅ Actualizar ambos stores para que se refleje en todo el sistema
    store.commit('auth/setUser', updatedUser);
    store.commit('setUser', updatedUser); // Store principal (para el dropdown)

    proxy.$notify(response.data.message, "", "success", "ni ni-like-2 ni-lg");

  } catch (error) {
    // Restaurar foto original si hay error
    photoUrl.value = props.data.user?.photo_url || photo_default;

    proxy.$notify(
      error.response?.data?.message || "Error al actualizar la foto.",
      "",
      "danger",
      "ni ni-fat-remove ni-lg"
    );
  }
};

const deleteProfilePhoto = async () => {
  if (!photoUrl.value || photoUrl.value === photo_default) {
    proxy.$notify("No hay foto para eliminar.", "", "warning", "ni ni-alert-circle-exc ni-lg");
    return;
  }

  // Confirmar antes de eliminar
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    html: '¡No podrás revertir esto!<br>¿Deseas eliminar tu foto de perfil?',
    icon: 'warning',
    showCancelButton: true,
    cancelButtonText: 'Cancelar',
    confirmButtonText: 'Sí, elimínala',
    customClass: {
      confirmButton: 'btn bg-gradient-success',
      cancelButton: 'btn bg-gradient-danger'
    },
    buttonsStyling: false
  });

  if (!result.isConfirmed) {
    return;
  }

  try {
    const response = await api.delete('/profile/photo');

    // Actualizar a foto por defecto
    photoUrl.value = photo_default;

    // Actualizar el store
    const currentUser = store.state.auth.user;
    const updatedUser = {
      ...currentUser,
      photo_url: null,
      photo: null
    };

    // ✅ Actualizar ambos stores para que se refleje en todo el sistema
    store.commit('auth/setUser', updatedUser);
    store.commit('setUser', updatedUser); // Store principal (para el dropdown)

    proxy.$notify(response.data.message, "", "success", "ni ni-like-2 ni-lg");

  } catch (error) {
    proxy.$notify(
      error.response?.data?.message || "Error al eliminar la foto.",
      "",
      "danger",
      "ni ni-fat-remove ni-lg"
    );
  }
};
</script>

<template>
  <div id="profile" class="card card-body mt-4">
    <div class="row justify-content-center align-items-center">
      <div class="col-sm-auto col-4">
        <div class="avatar-container" @click="() => $refs.fileInput.click()">
          <argon-avatar :image="photoUrl" alt="team-3" size="xl" shadow="sm" border-radius="lg"
            class="clickable-avatar" />
          <input ref="fileInput" type="file" accept="image/*" style="display: none;" @change="updateProfilePhoto" />

          <!-- Botón para eliminar foto -->
          <button v-if="photoUrl && photoUrl !== photo_default" class="btn-delete-photo" title="Eliminar foto"
            @click.stop="deleteProfilePhoto">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
      <div class="col-sm-auto col-8 my-auto">
        <div class="h-100">
          <h5 class="mb-1 font-weight-bolder">{{ fullName }}</h5>
          <p class="mb-0 font-weight-bold text-sm">{{ userRoleDescription }}</p>
        </div>
      </div>
      <div class="col-sm-auto ms-sm-auto mt-sm-0 mt-3 d-flex">
        <label class="form-check-label mb-0 me-2">
          <small id="profileVisibility">Switch to invisible</small>
        </label>
        <argon-switch id="switch-invisible" name="Switch to Invisible" checked />
      </div>
    </div>
  </div>
  <div id="basic-info" class="card mt-4">
    <div class="card-header">
      <h5>Mi Perfil</h5>
    </div>
    <div class="card-body pt-0">
      <div class="row">
        <div class="col-6">
          <label class="form-label modern-label">
            <i class="fas fa-user me-2"></i>
            Nombre completo
          </label>
          <argon-input id="firstname" v-model="fullName" type="text" placeholder="Apellidos y nombres" />
          <p v-if="errors.name" class="text-danger text-xs mt-1"><i class="fas fa-exclamation-circle me-1"></i>{{
            errors.name }}</p>
        </div>
        <div class="col-6">
          <label class="form-label modern-label">
            <i class="fas fa-envelope me-2"></i>
            Email
          </label>
          <argon-input id="email" v-model="email" type="text" placeholder="Email" />
          <p v-if="errors.email" class="text-danger text-xs mt-1"><i class="fas fa-exclamation-circle me-1"></i>{{
            errors.email }}</p>
        </div>
      </div>

      <!-- Sección de Especialidades Destacada -->
      <div v-if="props.data.user?.doctor" class="specialties-section mt-4">
        <div class="specialties-header">
          <div class="section-icon">
            <i class="fas fa-user-md"></i>
          </div>
          <div class="header-content">
            <h6 class="specialties-title mb-1">
              <i class="fas fa-stethoscope me-2"></i>
              Especialidades Médicas
            </h6>
            <p class="specialties-subtitle mb-0">
              <i class="fas fa-info-circle me-1"></i>
              Selecciona una o más áreas de tu especialización profesional
            </p>
          </div>
        </div>
        <div class="specialties-input-wrapper">
          <div class="input-label-wrapper">
            <label class="input-label">
              <i class="fas fa-list-check me-2"></i>
              Especialidades seleccionadas
            </label>
            <span v-if="selectedSpecialties.length > 0" class="badge-count">
              {{ selectedSpecialties.length }}
            </span>
          </div>
          <ChoicesSelectMultiple id="choices-specialties" :key="specialtiesOptions.length" v-model="selectedSpecialties"
            :options="specialtiesOptions" />
          <transition name="fade">
            <p v-if="errors.specialties" class="error-message mt-2">
              <i class="fas fa-exclamation-triangle me-1"></i>{{ errors.specialties }}
            </p>
          </transition>
        </div>
      </div>

      <!-- Botón de Actualizar Perfil Mejorado -->
      <div class="profile-actions mt-4">
        <ArgonButton variant="gradient" color="primary" size="md" class="btn-update-profile" :button-type="'button'"
          @click="updateProfile">
          <i class="fas fa-save me-2"></i>
          Actualizar Perfil
        </ArgonButton>
      </div>
    </div>
  </div>
  <div id="password" class="card mt-4">
    <div class="card-header">
      <h5>Cambiar Contraseña</h5>
    </div>
    <div class="card-body pt-0">
      <label class="form-label">Contraseña actual</label>
      <PasswordInput id="password" v-model="currentPassword" placeholder="Contraseña actual"
        aria-label="Contraseña actual" />
      <label class="form-label">Nueva contraseña</label>
      <PasswordInput id="new-password" v-model="newPassword" placeholder="Nueva contraseña"
        aria-label="Nueva contraseña" />
      <label class="form-label">Confirmar nueva contraseña</label>
      <PasswordInput id="confirm-password" v-model="confirmPassword" placeholder="Confirmar nueva contraseña"
        aria-label="Confirmar nueva contraseña" />
      <h5 class="mt-5">Requisitos de la contraseña</h5>
      <p class="text-muted mb-2">
        Por favor, sigue esta guía para una contraseña segura:
      </p>
      <ul class="text-muted ps-4 mb-0 float-start">
        <li>
          <span class="text-sm">Al menos un carácter especial</span>
        </li>
        <li>
          <span class="text-sm">Mínimo 6 caracteres</span>
        </li>
        <li>
          <span class="text-sm">Al menos un número (se recomiendan 2)</span>
        </li>
        <li>
          <span class="text-sm">Cámbiala con frecuencia</span>
        </li>
      </ul>
      <argon-button class="float-end mt-6 mb-0" color="dark" variant="gradient" size="sm"
        @click="updatePassword">Actualizar
        contraseña</argon-button>
    </div>
  </div>
  <div id="prescription-config" class="card mt-4">
    <div class="card-header">
      <h5>Editar Receta</h5>
      <p class="text-sm mb-0">
        Configura y personaliza el formato de tus recetas médicas.
      </p>
    </div>
    <div class="card-body pt-0">
      <PDFLabels :prescription="{ id: props.data.user?.id }" :doctor="{ id: props.data.user?.doctor?.id }" />
    </div>
  </div>
  <div id="2fa" class="card mt-4">
    <div class="card-header d-flex">
      <h5 class="mb-0">Two-factor authentication</h5>
      <argon-badge color="success" class="ms-auto">Enabled</argon-badge>
    </div>
    <div class="card-body">
      <div class="d-flex">
        <p class="my-auto">Security keys</p>
        <p class="text-secondary text-sm ms-auto my-auto me-3">
          No Security Keys
        </p>
        <argon-button color="dark" variant="outline" size="sm" class="mb-0" type="button">Add</argon-button>
      </div>
      <hr class="horizontal dark" />
      <div class="d-flex">
        <p class="my-auto">SMS number</p>
        <p class="text-secondary text-sm ms-auto my-auto me-3">+4012374423</p>
        <argon-button color="dark" variant="outline" size="sm" class="mb-0" type="button">Edit</argon-button>
      </div>
      <hr class="horizontal dark" />
      <div class="d-flex">
        <p class="my-auto">Authenticator app</p>
        <p class="text-secondary text-sm ms-auto my-auto me-3">
          Not Configured
        </p>
        <argon-button color="dark" variant="outline" size="sm" class="mb-0" type="button">Set up</argon-button>
      </div>
    </div>
  </div>
  <!-- Sección de Plan y Módulos eliminada - Información movida a Suscripción y Pagos -->
  <!-- <div id="accounts" class="card mt-4 profile-section profile-section-plan">...</div> -->

  <!-- Sección: Suscripción y Pagos -->
  <div id="subscription" class="card mt-4 profile-section profile-section-subscription">
    <div class="card-header pb-0 profile-section-header">
      <div class="section-header-row">
        <div class="section-header-text">
          <h5 class="section-title">
            <i class="fas fa-credit-card me-2"></i>
            Suscripción y Pagos
          </h5>
          <p class="text-sm mb-0 section-subtitle">
            Administra tu suscripción, método de pago e historial de facturas
          </p>
        </div>
        <div class="section-header-actions">
          <button class="btn btn-sm btn-primary refresh-btn" :disabled="isLoading" aria-label="Actualizar suscripción"
            @click="refreshSubscription">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
          </button>
        </div>
      </div>
    </div>
    <div class="card-body">
      <!-- Estado de la Suscripción -->
      <div v-if="hasSubscriptionRecord" class="subscription-status-section mb-4">
        <div class="status-card-compact">
          <div class="status-row">
            <div class="status-left">
              <div class="status-icon" :class="getStatusIconClass()">
                <i :class="getStatusIcon()"></i>
              </div>
              <div class="status-info">
                <h6 class="mb-1">Estado de Suscripción</h6>
                <span class="badge" :class="getStatusBadge(subscriptionStatus).class">
                  {{ getStatusBadge(subscriptionStatus).text }}
                </span>
              </div>
            </div>
            <div class="status-right">
              <div class="dates-compact">
                <div class="date-compact">
                  <i class="fas fa-calendar-check text-success me-2"></i>
                  <div>
                    <small class="text-muted d-block">Inicio</small>
                    <strong class="d-block">{{ formatDate(subscription?.starts_at) }}</strong>
                  </div>
                </div>
                <div v-if="isInTrial" class="date-compact highlight-trial">
                  <i class="fas fa-hourglass-half text-warning me-2"></i>
                  <div>
                    <small class="text-muted d-block">Fin de prueba</small>
                    <strong class="d-block">{{ formatDate(subscription?.trial_ends_at) }}</strong>
                    <span class="badge badge-warning-soft mt-1">
                      <i class="fas fa-clock me-1"></i>{{ daysRemaining }} días restantes
                    </span>
                  </div>
                </div>
                <div v-if="subscription?.ends_at" class="date-compact">
                  <i class="fas fa-calendar-times text-danger me-2"></i>
                  <div>
                    <small class="text-muted d-block">Fin</small>
                    <strong class="d-block">{{ formatDate(subscription?.ends_at) }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Módulos del Plan -->
      <div v-if="hasSubscriptionRecord" class="plan-modules-section mb-4">
        <h6 class="mb-3">
          <i class="fas fa-puzzle-piece me-2"></i>
          Módulos Incluidos
        </h6>
        <div class="modules-grid-enhanced">
          <div v-for="module in includedModules" :key="module.id" class="module-card">
            <i class="fas fa-check-circle text-success me-2"></i>
            <span>{{ module.name }}</span>
          </div>
        </div>
      </div>

      <!-- Información de Facturación -->
      <div v-if="hasSubscriptionRecord" class="billing-info-section mb-4">
        <h6 class="mb-3">
          <i class="fas fa-file-invoice-dollar me-2"></i>
          Información de Facturación
        </h6>
        <div class="billing-card">
          <div class="billing-row">
            <span class="billing-label">Plan actual:</span>
            <span class="billing-value fw-bold">{{ planName }}</span>
          </div>
          <div v-if="conektaCustomerId" class="billing-row billing-row-mono">
            <span class="billing-label">Conekta Customer ID:</span>
            <span class="billing-value billing-value-mono">{{ conektaCustomerId }}</span>
          </div>
          <div class="billing-row">
            <span class="billing-label">Precio:</span>
            <span class="billing-value fw-bold text-success">
              {{ formatPrice(planPrice, planCurrency) }}
            </span>
          </div>
          <div class="billing-row">
            <span class="billing-label">Ciclo de facturación:</span>
            <span class="billing-value">
              <span class="badge bg-info">
                {{ subscription?.billing_cycle === 'yearly' ? 'Anual' : 'Mensual' }}
              </span>
            </span>
          </div>
          <div v-if="subscription?.billing_cycle === 'yearly'" class="billing-row billing-row-note">
            <span class="billing-label">Descuento anual:</span>
            <span class="billing-value">17% (paga 10 meses, obtén 2 gratis)</span>
          </div>
          <div v-if="primaryPaymentMethod" class="billing-row">
            <span class="billing-label">Método de pago:</span>
            <span class="billing-value">{{ primaryPaymentMethod }}</span>
          </div>
          <div v-if="subscription?.next_billing_date" class="billing-row">
            <span class="billing-label">Próximo cobro:</span>
            <span class="billing-value">{{ formatDate(subscription?.next_billing_date) }}</span>
          </div>
        </div>
      </div>

      <!-- Acciones de Suscripción -->
      <div v-if="hasSubscriptionActions" class="subscription-actions mb-4">
        <h6 class="mb-3">
          <i class="fas fa-cog me-2"></i>
          Administrar Suscripción
        </h6>
        <div class="actions-grid">
          <button v-if="canChangePlan" class="action-btn action-change-plan" @click="handleChangePlan">
            <i class="action-icon fas fa-sync-alt"></i>
            <span class="action-title">Cambiar Plan</span>
            <span class="action-desc">Actualiza o cambia tu plan</span>
          </button>

          <button v-if="canUpdatePayment" class="action-btn action-payment-method" @click="handleUpdatePaymentMethod">
            <i class="action-icon fas fa-credit-card"></i>
            <span class="action-title">Método de Pago</span>
            <span class="action-desc">Actualizar tarjeta</span>
          </button>

          <!-- Nuevo: Botón para pagar ahora -->
          <button v-if="canPayNow" class="action-btn action-pay-now" @click="handlePayNow">
            <i class="action-icon fas fa-bolt"></i>
            <span class="action-title">Pagar Ahora</span>
            <span class="action-desc">
              {{ subscriptionStatus === 'trialing' ? 'Activar inmediatamente' : 'Reactivar suscripción' }}
            </span>
          </button>

          <button v-if="canChangeBillingCycle" class="action-btn action-billing-cycle"
            @click="handleChangeBillingCycle">
            <i class="action-icon fas fa-calendar-alt"></i>
            <span class="action-title">Ciclo de Cobro</span>
            <span class="action-desc">Mensual o anual</span>
          </button>

          <button v-if="canCancelSubscription" class="action-btn action-cancel" @click="handleCancelSubscription">
            <i class="action-icon fas fa-times-circle"></i>
            <span class="action-title">Cancelar</span>
            <span class="action-desc">Terminar suscripción</span>
          </button>

          <button v-if="canReactivate" class="action-btn action-reactivate" @click="handleReactivateSubscription">
            <i class="action-icon fas fa-rotate-right"></i>
            <span class="action-title">Reactivar</span>
            <span class="action-desc">Recuperar tu suscripción</span>
          </button>
        </div>
      </div>

      <!-- Historial de Pagos -->
      <div v-if="hasSubscriptionRecord" class="payment-history-section">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">
            <i class="fas fa-history me-2"></i>
            Historial de Pagos
          </h6>
          <button class="btn btn-sm btn-outline-primary history-btn" @click="loadPaymentHistory">
            <i class="fas fa-download me-2"></i>
            Ver todos
          </button>
        </div>

        <div v-if="paymentHistory.length > 0" class="payments-list">
          <div v-for="payment in paymentHistory.slice(0, 5)" :key="payment.id"
            :class="['payment-item', getPaymentStatusClass(payment.status)]">
            <div class="payment-icon" :class="getPaymentStatusClass(payment.status)">
              <i :class="getPaymentIcon(payment.status)"></i>
            </div>
            <div class="payment-info">
              <p class="payment-description mb-0">{{ payment.description }}</p>
              <small class="text-muted">{{ formatDate(payment.created_at) }}</small>
            </div>
            <div class="payment-amount">
              <span class="amount" :class="getPaymentAmountClass(payment.status)">
                {{ formatPrice(payment.amount, payment.currency) }}
              </span>
              <span class="badge badge-sm" :class="getPaymentBadgeClass(payment.status)">
                {{ getPaymentStatusLabel(payment.status) }}
              </span>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-4 text-muted">
          <i class="fas fa-inbox fa-2x mb-2"></i>
          <p class="mb-0">No hay historial de pagos</p>
        </div>
      </div>

      <!-- Sin Suscripción -->
      <div v-if="!hasSubscriptionRecord" class="no-subscription-state text-center py-5">
        <i class="fas fa-exclamation-circle fa-4x text-warning mb-3"></i>
        <h5 class="mb-2">No tienes una suscripción activa</h5>
        <p class="text-muted mb-4">
          Suscríbete a un plan para acceder a todas las funcionalidades de iDAMedic
        </p>
        <a href="/account/subscription" class="btn btn-success btn-lg">
          <i class="fas fa-crown me-2"></i>
          Ver Planes Disponibles
        </a>
      </div>
    </div>
  </div>

  <div id="notifications" class="card mt-4">
    <div class="card-header">
      <h5>Notifications</h5>
      <p class="text-sm">
        Choose how you receive notifications. These notification settings apply
        to the things you’re watching.
      </p>
    </div>
    <div class="card-body pt-0">
      <div class="table-responsive">
        <table class="table mb-0">
          <thead>
            <tr>
              <th class="ps-1" colspan="4">
                <p class="mb-0">Activity</p>
              </th>
              <th class="text-center">
                <p class="mb-0">Email</p>
              </th>
              <th class="text-center">
                <p class="mb-0">Push</p>
              </th>
              <th class="text-center">
                <p class="mb-0">SMS</p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="ps-1" colspan="4">
                <div class="my-auto">
                  <span class="text-dark d-block text-sm">Mentions</span>
                  <span class="text-xs font-weight-normal">Notify when another user mentions you in a comment</span>
                </div>
              </td>
              <td>
                <argon-switch id="notify-email" name="Email"
                  class="form-check form-switch mb-0 d-flex align-items-center justify-content-center" checked />
              </td>
              <td>
                <argon-switch id="notify-push" name="Push"
                  class="form-check form-switch mb-0 d-flex align-items-center justify-content-center" />
              </td>
              <td>
                <argon-switch id="notify-sms" name="Sms"
                  class="form-check form-switch mb-0 d-flex align-items-center justify-content-center" />
              </td>
            </tr>
            <tr>
              <td class="ps-1" colspan="4">
                <div class="my-auto">
                  <span class="text-dark d-block text-sm">Comments</span>
                  <span class="text-xs font-weight-normal">Notify when another user comments your item.</span>
                </div>
              </td>
              <td>
                <argon-switch id="notify-comment-email" name="Email"
                  class="form-check form-switch mb-0 d-flex align-items-center justify-content-center" checked />
              </td>
              <td>
                <argon-switch id="notify-comment-push" name="Push"
                  class="form-check form-switch mb-0 d-flex align-items-center justify-content-center" checked />
              </td>
              <td>
                <argon-switch id="notify-comment-sms" name="SMS"
                  class="form-check form-switch mb-0 d-flex align-items-center justify-content-center" />
              </td>
            </tr>
            <tr>
              <td class="ps-1" colspan="4">
                <div class="my-auto">
                  <span class="text-dark d-block text-sm">Follows</span>
                  <span class="text-xs font-weight-normal">Notify when another user follows you.</span>
                </div>
              </td>
              <td>
                <argon-switch id="notify-follow-email" name="Email"
                  class="mb-0 d-flex align-items-center justify-content-center" checked />
              </td>
              <td>
                <argon-switch id="notify-follow-push" name="Push"
                  class="mb-0 d-flex align-items-center justify-content-center" checked />
              </td>
              <td>
                <argon-switch id="notify-follow-sms" name="SMS"
                  class="mb-0 d-flex align-items-center justify-content-center" checked />
              </td>
            </tr>
            <tr>
              <td class="ps-1" colspan="4">
                <div class="my-auto">
                  <p class="text-sm mb-0">Log in from a new device</p>
                </div>
              </td>
              <td>
                <argon-switch id="notify-login-email" name="Email"
                  class="mb-0 d-flex align-items-center justify-content-center" checked />
              </td>
              <td>
                <argon-switch id="notify-login-push" name="Push"
                  class="mb-0 d-flex align-items-center justify-content-center" checked />
              </td>
              <td>
                <argon-switch id="notify-login-sms" name="SMS"
                  class="mb-0 d-flex align-items-center justify-content-center" checked />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  <div id="sessions" class="card mt-4">
    <div class="card-header pb-3">
      <h5>Sessions</h5>
      <p class="text-sm">
        This is a list of devices that have logged into your account. Remove
        those that you do not recognize.
      </p>
    </div>
    <div class="card-body pt-0">
      <div class="d-flex align-items-center">
        <div class="text-center w-5">
          <i class="fas fa-desktop text-lg opacity-6"></i>
        </div>
        <div class="my-auto ms-3">
          <div class="h-100">
            <p class="text-sm mb-1">Bucharest 68.133.163.201</p>
            <p class="mb-0 text-xs">Your current session</p>
          </div>
        </div>
        <argon-badge color="success" size="sm" class="my-auto ms-auto me-3">Active</argon-badge>
        <p class="text-secondary text-sm my-auto me-3">EU</p>
        <a href="javascript:;" class="text-primary text-sm icon-move-right my-auto">
          See more
          <i class="fas fa-arrow-right text-xs ms-1" aria-hidden="true"></i>
        </a>
      </div>
      <hr class="horizontal dark" />
      <div class="d-flex align-items-center">
        <div class="text-center w-5">
          <i class="fas fa-desktop text-lg opacity-6"></i>
        </div>
        <p class="my-auto ms-3">Chrome on macOS</p>
        <p class="text-secondary text-sm ms-auto my-auto me-3">US</p>
        <a href="javascript:;" class="text-primary text-sm icon-move-right my-auto">
          See more
          <i class="fas fa-arrow-right text-xs ms-1" aria-hidden="true"></i>
        </a>
      </div>
      <hr class="horizontal dark" />
      <div class="d-flex align-items-center">
        <div class="text-center w-5">
          <i class="fas fa-mobile text-lg opacity-6"></i>
        </div>
        <p class="my-auto ms-3">Safari on iPhone</p>
        <p class="text-secondary text-sm ms-auto my-auto me-3">US</p>
        <a href="javascript:;" class="text-primary text-sm icon-move-right my-auto">
          See more
          <i class="fas fa-arrow-right text-xs ms-1" aria-hidden="true"></i>
        </a>
      </div>
    </div>
  </div>
  <div id="delete" class="card mt-4">
    <div class="card-header">
      <h5>Delete Account</h5>
      <p class="text-sm mb-0">
        Once you delete your account, there is no going back. Please be certain.
      </p>
    </div>
    <div class="card-body d-sm-flex pt-0">
      <div class="d-flex align-items-center mb-sm-0 mb-4">
        <div>
          <argon-switch id="delete-account" name="Delete Account" />
        </div>
        <div class="ms-2">
          <span class="text-dark font-weight-bold d-block text-sm">Confirm</span>
          <span class="text-xs d-block">I want to delete my account.</span>
        </div>
      </div>
      <argon-button color="secondary" variant="outline" class="mb-0 ms-auto" type="button"
        name="button">Deactivate</argon-button>
      <argon-button color="danger" variant="gradient" class="mb-0 ms-2" type="button" name="button">Delete
        Account</argon-button>
    </div>
  </div>
</template>

<style scoped>
/* Estilos modernos para labels */
.modern-label {
  font-weight: 600;
  color: #344767;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
}

.modern-label i {
  color: #67748e;
  font-size: 0.875rem;
}

/* Sección de Especialidades - Diseño Simple */
.specialties-section {
  background: #ffffff;
  border-radius: 0.75rem;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  position: relative;
  overflow: visible;
  z-index: 100;
}

.specialties-section:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
}

.specialties-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.section-icon {
  width: 40px;
  height: 40px;
  background: #6b7280;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.specialties-section:hover .section-icon {
  background: #4b5563;
}

.section-icon i {
  color: white;
  font-size: 1.125rem;
}

.header-content {
  flex: 1;
}

.specialties-title {
  font-size: 1rem;
  font-weight: 700;
  color: #374151;
  margin: 0;
  display: flex;
  align-items: center;
  letter-spacing: -0.01em;
}

.specialties-title i {
  color: #6b7280;
  font-size: 0.875rem;
}

.specialties-subtitle {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0.25rem 0 0 0;
  display: flex;
  align-items: center;
  line-height: 1.5;
  font-weight: 400;
}

.specialties-subtitle i {
  color: #9ca3af;
  font-size: 0.75rem;
}

.specialties-input-wrapper {
  position: relative;
  background: #f9fafb;
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  overflow: visible;
  z-index: 150;
}

.specialties-input-wrapper:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  z-index: 200;
}

.input-label-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.input-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  margin: 0;
}

.input-label i {
  color: #6b7280;
  font-size: 0.875rem;
}

.badge-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 0.5rem;
  background: #6b7280;
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.error-message {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #dc3545;
  background: #fff5f5;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border-left: 3px solid #dc3545;
}

.error-message i {
  flex-shrink: 0;
  color: #dc3545;
}

/* Animación fade para errores */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Botón de Actualizar Perfil Mejorado */
.profile-actions {
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
}

.btn-update-profile {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  padding: 0.8rem 1.8rem !important;
  border-radius: 12px !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  transition: all 0.3s ease !important;
}

.btn-update-profile:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(94, 114, 228, 0.4) !important;
}

.btn-update-profile i {
  font-size: 0.95rem;
}

/* Mejorar mensajes de error */
.text-danger {
  display: flex;
  align-items: center;
  font-weight: 500;
}

/* Secciones de plan y suscripción */
.profile-section {
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.profile-section-header {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 60%, #ffffff 100%);
  border-bottom: 1px solid #eef2f7;
}

.section-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.section-header-text {
  flex: 1 1 320px;
  min-width: 220px;
}

.section-title {
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.section-subtitle {
  color: #64748b;
}

.section-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
}

.plan-badge-slot {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
}

.refresh-btn {
  border-radius: 10px !important;
  border: 1px solid #c7d2fe !important;
  background: linear-gradient(135deg, #eef2ff 0%, #ffffff 100%) !important;
  color: #4338ca !important;
  box-shadow: 0 10px 18px rgba(67, 56, 202, 0.15);
  padding: 0.35rem 0.6rem;
}

.refresh-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 24px rgba(67, 56, 202, 0.18);
}

.refresh-btn:disabled {
  opacity: 0.6;
  box-shadow: none;
}

/* Plan y módulos */
.plan-summary-card {
  background: linear-gradient(140deg, #f8fafc 0%, #ffffff 55%, #eef2ff 100%) !important;
  border: 1px solid #e2e8f0;
  border-radius: 16px !important;
  padding: 1.5rem !important;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
}

.plan-summary-card::before {
  content: "";
  position: absolute;
  top: -45%;
  right: -35%;
  width: 240px;
  height: 240px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.16), transparent 65%);
  pointer-events: none;
}

.plan-summary-header {
  position: relative;
  z-index: 1;
}

.plan-title {
  color: #0f172a;
  letter-spacing: -0.01em;
}

.plan-description {
  color: #64748b;
}

.plan-status-badge {
  border-radius: 999px !important;
  font-weight: 700;
  padding: 0.35rem 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 0.65rem;
}

.plan-meta-row {
  background: rgba(255, 255, 255, 0.75);
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
  padding: 0.5rem 0.75rem;
}

.plan-meta-value {
  color: #0f172a;
}

.plan-discount-note {
  margin-top: 0.35rem;
  color: #64748b;
}

.plan-modules {
  position: relative;
  z-index: 1;
}

.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.65rem;
  margin-top: 0.75rem;
}

.module-badge {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.05);
  transition: all 0.2s ease;
}

.module-badge:hover {
  border-color: #22c55e;
  box-shadow: 0 10px 20px rgba(34, 197, 94, 0.16);
  transform: translateY(-1px);
}

.module-badge i {
  flex-shrink: 0;
}

.module-badge span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Nuevos estilos para módulos en sección Suscripción y Pagos */
.plan-modules-section h6 {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.modules-grid-enhanced {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
}

.module-card {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.module-card:hover {
  border-color: #22c55e;
  background: #f0fdf4;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.12);
  transform: translateY(-1px);
}

.module-card i {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.trial-alert {
  background: linear-gradient(135deg, #ecfeff 0%, #f0f9ff 100%);
  border: 1px solid #bae6fd;
  color: #0f172a;
  border-radius: 12px;
}

.trial-alert i {
  color: #0ea5e9;
}

.plan-empty-alert {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 12px;
}

.plan-empty-alert .alert-link {
  color: #b45309;
  font-weight: 600;
}


.text-danger i {
  flex-shrink: 0;
}

/* Mejorar aspecto del avatar clickeable */
.avatar-container {
  position: relative;
  display: inline-block;
  cursor: pointer;
}

.clickable-avatar {
  transition: all 0.3s ease;
  pointer-events: none;
  /* Permite que el click pase al contenedor */
}

.avatar-container:hover .clickable-avatar {
  transform: scale(1.05);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15) !important;
}

/* Botón para eliminar foto */
.btn-delete-photo {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  border: 3px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
  z-index: 10;
}

.btn-delete-photo:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.6);
}

.btn-delete-photo i {
  color: white;
  font-size: 0.75rem;
}

/* Estilos para sección de suscripción - Diseño Compacto */
.status-card-compact {
  padding: 1.25rem;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.status-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.status-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 0 0 auto;
}

.status-right {
  flex: 1 1 auto;
  min-width: 300px;
}

.dates-compact {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: flex-start;
}

.date-compact {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  flex: 1 1 auto;
  min-width: 180px;
}

.date-compact.highlight-trial {
  background: #fffbeb;
  border-color: #fde047;
}

.date-compact small {
  font-size: 0.7rem;
  line-height: 1.2;
  margin-bottom: 0.15rem;
}

.date-compact strong {
  font-size: 0.9rem;
  line-height: 1.3;
  color: #0f172a;
}

.date-compact i {
  font-size: 1.1rem;
  margin-top: 0.15rem;
}

.badge-warning-soft {
  background: #fef3c7;
  color: #92400e;
  font-size: 0.65rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

/* Mantener estilos originales de status-card para compatibilidad */
.status-card {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 55%, #eef2ff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  color: #0f172a;
  margin-bottom: 1.5rem;
  box-shadow: 0 14px 26px rgba(15, 23, 42, 0.08);
  position: relative;
  overflow: hidden;
}

.status-card::after {
  content: "";
  position: absolute;
  top: -45%;
  right: -30%;
  width: 260px;
  height: 260px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.18), transparent 65%);
  pointer-events: none;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  position: relative;
  z-index: 1;
}

.status-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.12);
  background: #ffffff;
}

.status-icon-active {
  background: #dcfce7;
  color: #16a34a;
}

.status-icon-trial {
  background: #fef9c3;
  color: #ca8a04;
}

.status-icon-warning {
  background: #fee2e2;
  color: #dc2626;
}

.status-icon-inactive {
  background: #f1f5f9;
  color: #64748b;
}

.status-info h6 {
  margin: 0 0 0.25rem 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.status-info .badge {
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.65rem;
  letter-spacing: 0.04em;
}

.status-info .badge-success {
  background: #dcfce7;
  color: #166534;
}

.status-info .badge-info {
  background: #e0f2fe;
  color: #075985;
}

.status-info .badge-warning {
  background: #fef3c7;
  color: #92400e;
}

.status-info .badge-danger {
  background: #fee2e2;
  color: #991b1b;
}

.status-info .badge-secondary {
  background: #e2e8f0;
  color: #475569;
}

.subscription-dates,
.status-dates {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 0.75rem;
  position: relative;
  z-index: 1;
}

.date-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0.85rem;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.05);
}

.date-item i {
  font-size: 1.1rem;
  margin-top: 0.1rem;
}

.date-item small {
  display: block;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.date-item p {
  margin: 0;
  font-weight: 700;
  color: #0f172a;
}

.billing-card {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 60%, #ecfeff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.25rem;
  color: #0f172a;
  margin-bottom: 1.5rem;
  box-shadow: 0 12px 22px rgba(15, 23, 42, 0.06);
}

.billing-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0;
  border-bottom: 1px dashed #e2e8f0;
}

.billing-row-mono {
  align-items: flex-start;
  gap: 0.75rem;
}

.billing-row-note .billing-value {
  color: #0f766e;
  font-weight: 600;
}

.billing-row:last-child {
  border-bottom: none;
}

.billing-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.billing-value {
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
}

.billing-value-mono {
  font-family: "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.85rem;
  word-break: break-all;
}

.billing-value .badge {
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 999px;
  padding: 0.2rem 0.6rem;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.action-btn {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.06);
  --action-glow: rgba(59, 130, 246, 0.18);
}

.action-btn::after {
  content: "";
  position: absolute;
  top: -50%;
  right: -20%;
  width: 180px;
  height: 180px;
  background: radial-gradient(circle, var(--action-glow), transparent 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.action-btn:hover {
  border-color: #c7d2fe;
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.12);
  transform: translateY(-2px);
}

.action-btn:hover::after {
  opacity: 1;
}

.action-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-bottom: 0.75rem;
}

.action-title {
  display: block;
  font-weight: 700;
  color: #0f172a;
  font-size: 0.95rem;
}

.action-desc {
  display: block;
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 0.25rem;
}

.action-change-plan {
  --action-glow: rgba(14, 165, 233, 0.22);
}

.action-change-plan .action-icon {
  background: #e0f2fe;
  color: #0284c7;
}

.action-payment-method {
  --action-glow: rgba(234, 179, 8, 0.18);
}

.action-payment-method .action-icon {
  background: #fef9c3;
  color: #a16207;
}

.action-billing-cycle {
  --action-glow: rgba(139, 92, 246, 0.18);
}

.action-billing-cycle .action-icon {
  background: #ede9fe;
  color: #7c3aed;
}

.action-pay-now {
  --action-glow: rgba(16, 185, 129, 0.2);
  border-color: #d1fae5;
}

.action-pay-now .action-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  animation: pulse-glow 2s ease-in-out infinite;
}

.action-pay-now:hover {
  border-color: #a7f3d0;
  box-shadow: 0 8px 16px rgba(16, 185, 129, 0.25);
}

@keyframes pulse-glow {

  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
  }

  50% {
    box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
  }
}

.action-cancel {
  --action-glow: rgba(239, 68, 68, 0.18);
  border-color: #fee2e2;
}

.action-cancel .action-icon {
  background: #fee2e2;
  color: #dc2626;
}

.action-cancel:hover {
  border-color: #fca5a5;
}

.action-reactivate {
  --action-glow: rgba(34, 197, 94, 0.2);
  border-color: #dcfce7;
}

.action-reactivate .action-icon {
  background: #dcfce7;
  color: #16a34a;
}

.action-reactivate:hover {
  border-color: #86efac;
}

.payment-history-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.25rem;
}

.history-btn {
  border-radius: 999px !important;
  border: 1px solid #c7d2fe !important;
  color: #4338ca !important;
  background: #ffffff !important;
}

.history-btn:hover {
  background: #eef2ff !important;
}

.payments-list {
  display: grid;
  gap: 0.75rem;
}

.payment-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.9rem;
  padding: 0.85rem 1rem;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
}

.payment-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #cbd5f5;
  border-radius: 14px 0 0 14px;
}

.payment-item:hover {
  border-color: #c7d2fe;
  transform: translateY(-1px);
}

.payment-item.payment-icon-success::before {
  background: #22c55e;
}

.payment-item.payment-icon-pending::before {
  background: #f59e0b;
}

.payment-item.payment-icon-refunded::before {
  background: #64748b;
}

.payment-item.payment-icon-failed::before {
  background: #ef4444;
}

.payment-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f1f5f9;
  box-shadow: inset 0 0 0 1px #e2e8f0;
}

.payment-icon.payment-icon-success {
  background: #dcfce7;
  color: #16a34a;
}

.payment-icon.payment-icon-pending {
  background: #fef3c7;
  color: #b45309;
}

.payment-icon.payment-icon-refunded {
  background: #e2e8f0;
  color: #475569;
}

.payment-icon.payment-icon-failed {
  background: #fee2e2;
  color: #dc2626;
}

.payment-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.payment-description {
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0;
}

.payment-amount {
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.payment-amount .amount {
  font-weight: 800;
  font-size: 1rem;
  color: #0f172a;
}

.payment-amount .badge {
  border-radius: 999px;
  font-size: 0.65rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.no-subscription-state {
  background: linear-gradient(135deg, #fff7ed 0%, #ffffff 70%);
  border: 1px dashed #fdba74;
  border-radius: 16px;
  padding: 2rem 1.5rem;
}

.no-subscription-state h5 {
  color: #92400e;
  font-weight: 700;
}

.no-subscription-state p {
  color: #9a3412;
}

.no-subscription-state .btn {
  box-shadow: 0 12px 24px rgba(16, 185, 129, 0.2);
}

@media (max-width: 576px) {
  .section-header-row {
    flex-direction: column;
    align-items: stretch;
  }

  .section-header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .payment-item {
    grid-template-columns: auto 1fr;
  }

  .payment-amount {
    grid-column: 1 / -1;
    align-items: flex-start;
    text-align: left;
  }
}
</style>
